import { FC, useMemo } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	Alert,
	AlertBody,
	AlertDescription,
	AlertIcon,
	Button,
	Center,
	DatePicker,
	Divider,
	Dropdown,
	DropdownInput,
	DropdownMenu,
	DropdownOption,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Heading,
	HStack,
	IconButton,
	Image,
	Input,
	InputGroup,
	InputRightElement,
	LockClosedOutline,
	Radio,
	RadioGroup,
	rem,
	Spacer,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
	TrashOutline,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';
import { useCurrentUser } from '@bonusly/who-am-i';

import { getDefaultIcon, getDefaultImage } from '../../components/celebrations/utils';
import { Conceal } from '../../components/display/conceal';
import { Reveal } from '../../components/display/reveal';
import { CountHelpText } from '../../components/form/count-help-text';
import { FormSection } from '../../components/form/form-section';
import { InfoLabel } from '../../components/form/info-label';
import { UploadButton } from '../../components/form/upload-button';
import { CheckboxRuleSet } from '../../components/groups/checkbox-rule-set';
import { useGroupRulesControl, useSelectedGroups } from '../../components/groups/hooks';
import { CelebrationType, CelebrationVisibility, CelebrationWorkAnniversaryPointSetting } from '../../entities/celebrations';
import { useUserGroups } from '../../queries/groups';
import { trackClick } from '../../utils/analytics';
import { dateIsBeforeToday } from '../../utils/date';
import { generateFileURL } from '../../utils/file';
import { ADDING_GROUPS_URL } from './constants';
import { FeedPreview } from './feed-preview';
import { CelebrationFormData } from './types';

type CelebrationFormProps = {
	disabledValues: string[];
	forceScope: string | undefined;
	form: ReturnType<typeof useForm<CelebrationFormData>>;
	isLoading: boolean;
	isSubmitDisabled?: boolean;
	onSubmit: (data: CelebrationFormData) => void;
	onDelete?: () => void;
	isDisabled?: boolean;
};

export const CelebrationForm: FC<CelebrationFormProps> = ({
	disabledValues,
	forceScope,
	form,
	isDisabled = false,
	isLoading,
	isSubmitDisabled = false,
	onSubmit,
	onDelete,
}) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'form' });
	const { data: rules } = useUserGroups();
	const user = useCurrentUser();

	assertPresence(rules);

	const hasGroupsToSelect = rules.departments.length > 0 || rules.locations.length > 0 || rules.teams.length > 0;

	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
		watch,
	} = form;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'yearAmountMappings',
	});

	const [name, headline, reason, amount, image, icon, mappings, type, recipientDepartments, recipientLocations, recipientTeams, workAnniversaryPointSetting] =
		watch([
			'name',
			'headline',
			'reason',
			'amount',
			'image',
			'icon',
			'yearAmountMappings',
			'type',
			'recipientDepartments',
			'recipientLocations',
			'recipientTeams',
			'workAnniversaryPointSetting',
		]);

	const recipientControl = useGroupRulesControl('recipient', form, { forceScope });

	const iconURL = generateFileURL(icon);
	const imageURL = generateFileURL(image);

	const handleClickAddYear = () => {
		const yearThreshold = Math.max(...mappings.map((m) => m.yearThreshold ?? 0), 0) + 1;
		const amount = Math.max(...mappings.map((m) => m.amount ?? 0), 0);
		append({ yearThreshold, amount });
	};

	const { groupContext: selectedRecipientsGroupContext, selectedGroups: selectedRecipientsGroups } = useSelectedGroups(
		recipientControl,
		recipientLocations,
		recipientDepartments,
		recipientTeams,
		[],
		rules
	);

	const recipientRulesContext = [selectedRecipientsGroupContext, type !== CelebrationType.Wellness && type].filter(Boolean).join('_');

	const yearList = useMemo(() => {
		const sortedMappings = mappings.toSorted((a, b) => a.yearThreshold - b.yearThreshold);
		const gapYears: string[] = [];
		let previousYearThreshhold = 0;

		sortedMappings.forEach((mapping) => {
			const currentYearThreshhold = mapping.yearThreshold;

			if (isNaN(currentYearThreshhold)) {
				return;
			}

			if (currentYearThreshhold > previousYearThreshhold + 1) {
				const gapSize = currentYearThreshhold - previousYearThreshhold - 1;

				if (gapSize == 1) {
					gapYears.push(`${previousYearThreshhold + 1}`);
				} else {
					gapYears.push(`${previousYearThreshhold + 1}-${currentYearThreshhold - 1}`);
				}
			}
			previousYearThreshhold = currentYearThreshhold;
		});

		gapYears.push(`${previousYearThreshhold + 1}`);

		return gapYears;
	}, [mappings, t]);

	const handleChangeWorkAnniversaryPointSetting = (value: any, field: any) => {
		if (value !== CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount && mappings.length === 0) {
			append({ yearThreshold: 1, amount: 100 });
		}
		field.onChange(value);
	};

	const reasonMessageHelperKey = () => {
		const requiredValuesKey =
			!!amount || (type === CelebrationType.WorkAnniversary && workAnniversaryPointSetting !== CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount)
				? 'recipientAndAmount'
				: 'recipient';

		if (type === CelebrationType.Custom) {
			return `${type}_${requiredValuesKey}`;
		} else {
			return requiredValuesKey;
		}
	};

	const DefaultIcon = getDefaultIcon(type);
	const DefaultImage = getDefaultImage(type);

	return (
		<FormProvider {...form}>
			<Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={6}>
				<FormSection>
					<Heading size="xl" color={isDisabled ? 'gray.500' : undefined}>
						{t('basicInfo')}
					</Heading>

					<FormControl isInvalid={!!errors.name} isDisabled={isDisabled}>
						<FormLabel _disabled={{ color: 'gray.500' }}>{t('nameLabel')}</FormLabel>
						<Input type="text" {...register('name')} />
						<CountHelpText count={name.trim().length} max={40} />
					</FormControl>

					<Conceal if={type === CelebrationType.Custom}>
						<FormControl isInvalid={!!errors.type} isDisabled={isDisabled}>
							<FormLabel _disabled={{ color: 'gray.500' }}>{t('basedOnLabel')}</FormLabel>

							<HStack>
								<Controller
									control={control}
									name="type"
									render={({ field }) => {
										return (
											<InputGroup>
												<Input
													type="text"
													isReadOnly
													disabled
													value={t('basedOn', { context: type })}
													onChange={field.onChange}
													ref={field.ref}
													borderColor="gray.500"
													color="gray.500"
												/>
												<InputRightElement pointerEvents="none">
													<LockClosedOutline color="gray.300" size="sm" />
												</InputRightElement>
											</InputGroup>
										);
									}}
								/>
							</HStack>

							<FormHelperText color={isDisabled ? 'gray.500' : undefined}>{t('basedOnHelper')}</FormHelperText>
						</FormControl>
					</Conceal>

					<Reveal if={type === CelebrationType.Custom}>
						<FormControl isInvalid={!!errors.celebrationDate} isDisabled={isDisabled}>
							<FormLabel _disabled={{ color: 'gray.500' }}>{t('postOnLabel')}</FormLabel>
							<Controller
								control={control}
								name="celebrationDate"
								render={({ field }) => <DatePicker disableDateIf={dateIsBeforeToday} w="full" {...field} isClearable={true} />}
							/>
							<FormHelperText>{t('postOnHelper')}</FormHelperText>
						</FormControl>
					</Reveal>

					<FormControl isInvalid={!!errors.visibility} isDisabled={isDisabled}>
						<FormLabel _disabled={{ color: 'gray.500' }}>{t('postVisibilityLabel')}</FormLabel>
						<Controller
							control={control}
							name="visibility"
							render={({ field }) => (
								<Dropdown
									placeholder={t('dropdownPlaceholder')}
									placement="bottom-start"
									onChange={field.onChange}
									value={field.value}
									valueToString={(value: string) => t(`postVisibilityValues.${value}`)}
								>
									<DropdownInput w="full" disabled={isDisabled} />

									<DropdownMenu>
										<DropdownOption value={CelebrationVisibility.Public}>{t('postVisibilityValues.publicly_visible')}</DropdownOption>
										<DropdownOption value={CelebrationVisibility.Team}>{t('postVisibilityValues.immediate_team')}</DropdownOption>
										<DropdownOption value={CelebrationVisibility.Private}>{t('postVisibilityValues.privately_visible')}</DropdownOption>
									</DropdownMenu>
								</Dropdown>
							)}
						/>
						<Reveal if={type === CelebrationType.WorkAnniversary || type === CelebrationType.Birthday}>
							<FormHelperText>{t('postVisibilityHelper')}</FormHelperText>
						</Reveal>
					</FormControl>

					<FormControl isDisabled={isDisabled}>
						<FormLabel mb={1} style={{ opacity: 'unset' }} _disabled={{ color: 'gray.500' }}>
							{t('recipientLabel')}
						</FormLabel>
						<FormHelperText mt={0} mb={2}>
							{t('recipientHelper')}
						</FormHelperText>

						<Reveal if={selectedRecipientsGroupContext && selectedRecipientsGroups.length}>
							<Alert status="info" size="sm" mb={1.5}>
								<AlertIcon />
								<AlertBody>
									<AlertDescription>
										<Trans
											t={t}
											i18nKey={`recipientRulesHelper_${recipientRulesContext}`}
											context={recipientRulesContext}
											values={{ items: selectedRecipientsGroups }}
											shouldUnescape={true}
										>
											All users in
											<Text as="span" size="sm">
												{{ selectedRecipientsGroups } as any}
											</Text>
											departments are eligible to receive this celebration post.
										</Trans>
									</AlertDescription>
								</AlertBody>
							</Alert>
						</Reveal>

						<Reveal if={forceScope}>
							<Alert status="info" size="sm" mb={1.5}>
								<AlertIcon />
								<AlertBody>
									<AlertDescription>{t('scopeHelper', { context: forceScope })}</AlertDescription>
								</AlertBody>
							</Alert>
						</Reveal>

						<Stack spacing={2}>
							<Radio isDisabled={!!forceScope} {...recipientControl.anyone}>
								{t('everyoneLabel')}
							</Radio>
							<Reveal if={hasGroupsToSelect}>
								<CheckboxRuleSet
									disabledValues={disabledValues}
									isDisabled={!!forceScope}
									name="recipientLocations"
									options={rules.locations}
									ref={register('recipientLocations').ref}
									{...recipientControl.locations}
								>
									{t('locationsLabel')}
								</CheckboxRuleSet>

								<CheckboxRuleSet
									disabledValues={disabledValues}
									isDisabled={!!forceScope}
									name="recipientDepartments"
									options={rules.departments}
									ref={register('recipientDepartments').ref}
									{...recipientControl.departments}
								>
									{t('departmentsLabel')}
								</CheckboxRuleSet>

								<CheckboxRuleSet
									disabledValues={disabledValues}
									isDisabled={!!forceScope}
									name="recipientTeams"
									options={rules.teams}
									ref={register('recipientTeams').ref}
									{...recipientControl.teams}
								>
									{t('teamsLabel')}
								</CheckboxRuleSet>
							</Reveal>
							<Reveal if={errors.recipientDepartments || errors.recipientLocations || errors.recipientTeams}>
								<Alert status="error" size="sm" mb={1.5}>
									<AlertIcon />
									<AlertBody>
										<AlertDescription>{t('scopeError')}</AlertDescription>
									</AlertBody>
								</Alert>
							</Reveal>

							<Conceal if={hasGroupsToSelect}>
								<Text size="sm">
									<Trans i18nKey="hrisConnectionMessage" t={t}>
										We don’t have any group information (department, location, or team) for your users yet. To target specific groups, you’ll need to provide
										that data by connecting your HR system, adding this information to your SFTP file, or entering it manually for each user.
										<Text
											size="sm"
											as={Link}
											color="text.interactive"
											to={ADDING_GROUPS_URL}
											target="_blank"
											rel="noopener noreferrer"
											{...trackClick('View HRIS Connections from Celebrations')}
										>
											Learn how to provide group information.
										</Text>
									</Trans>
								</Text>
							</Conceal>
						</Stack>
					</FormControl>
				</FormSection>

				<FormSection>
					<Heading size="xl" color={isDisabled ? 'gray.500' : undefined}>
						{t('points.heading')}
					</Heading>

					{type === CelebrationType.WorkAnniversary ? (
						<>
							<FormLabel>{t('points.workAnniversaryPointSetting.label')}</FormLabel>
							<FormControl isDisabled={isDisabled}>
								<Controller
									control={control}
									name="workAnniversaryPointSetting"
									render={({ field }) => (
										<RadioGroup value={field.value} onChange={(value) => handleChangeWorkAnniversaryPointSetting(value, field)}>
											<Stack spacing={2}>
												<Radio value={CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount}>
													{t('points.workAnniversaryPointSetting.everyYearWithSameAmount')}
												</Radio>
												<Radio value={CelebrationWorkAnniversaryPointSetting.EveryYearWithCustomAmount}>
													{t('points.workAnniversaryPointSetting.everyYearWithCustomAmount')}
												</Radio>
												<Radio value={CelebrationWorkAnniversaryPointSetting.OnlySpecificYears}>
													{t('points.workAnniversaryPointSetting.onlySpecificYears')}
												</Radio>
											</Stack>
										</RadioGroup>
									)}
								/>
							</FormControl>

							<Reveal if={workAnniversaryPointSetting !== CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount}>
								<FormLabel>{t('points.milestoneLabel')}</FormLabel>

								<Table size="sm">
									<Thead>
										<Tr sx={{ th: { borderBottom: 'none', px: 1, _first: { pl: 0 }, _last: { pr: 0 } } }}>
											<Th width={rem(160)}>{t('points.yearLabel')}</Th>
											<Th>{t('points.amountLabel')}</Th>
											<Th width={0} />
										</Tr>
									</Thead>

									<Tbody>
										{fields.map((field, index) => {
											return (
												<Tr key={field.id} px={0} sx={{ td: { px: 1, _first: { pl: 0 }, _last: { pr: 0 } } }} _last={{ td: { borderBottom: 'none', pb: 0 } }}>
													<Td width={rem(160)}>
														<FormControl isInvalid={!!errors.yearAmountMappings?.[index]?.yearThreshold} isDisabled={isDisabled}>
															<Input {...register(`yearAmountMappings.${index}.yearThreshold`, { valueAsNumber: true })} />
														</FormControl>
													</Td>
													<Td>
														<FormControl isInvalid={!!errors.yearAmountMappings?.[index]?.amount} isDisabled={isDisabled}>
															<Input {...register(`yearAmountMappings.${index}.amount`, { valueAsNumber: true })} />
														</FormControl>
													</Td>
													<Td width={0}>
														{!!index && (
															<IconButton
																aria-label="Remove celebration for anniversary"
																isRound
																onClick={() => remove(index)}
																boxSize={rem(32)}
																minWidth="unset"
																isDisabled={isDisabled}
															>
																<TrashOutline size="sm" />
															</IconButton>
														)}
													</Td>
												</Tr>
											);
										})}
									</Tbody>
								</Table>

								<HStack>
									<Button size="sm" onClick={handleClickAddYear} isDisabled={isDisabled}>
										{t('points.addYearButton')}
									</Button>
								</HStack>
							</Reveal>

							<Reveal if={workAnniversaryPointSetting !== CelebrationWorkAnniversaryPointSetting.OnlySpecificYears}>
								<FormControl isInvalid={!!errors.amount} isDisabled={isDisabled}>
									<InfoLabel label={t('points.workAnniversaryAmountTooltip')}>
										{t('points.workAnniversaryAmountLabel', { context: workAnniversaryPointSetting })}
									</InfoLabel>
									<Input {...register('amount', { valueAsNumber: true })} />
								</FormControl>
							</Reveal>

							<Alert status="info" size="sm">
								<AlertIcon />
								<AlertBody>
									<AlertDescription>{t('points.workAnniversaryAmountHelper', { context: workAnniversaryPointSetting, amount, yearList })}</AlertDescription>
								</AlertBody>
							</Alert>
						</>
					) : (
						<FormControl isInvalid={!!errors.amount} isDisabled={isDisabled}>
							<InfoLabel label={t('points.zeroPointsHelper', { context: type })}>
								<Text color={isDisabled ? 'gray.500' : undefined}>{t('points.amountLabel')}</Text>
							</InfoLabel>
							<Input maxW={rem(310)} {...register('amount', { valueAsNumber: true })} />
						</FormControl>
					)}
				</FormSection>

				<FormSection>
					<HStack>
						<Heading color={isDisabled ? 'gray.500' : undefined} size="xl">
							{t('postCustomization.heading')}
						</Heading>
					</HStack>

					<FormControl isInvalid={!!errors.icon} isDisabled={isDisabled}>
						<FormLabel>{t('postCustomization.iconLabel')}</FormLabel>
						<HStack spacing={5}>
							<Center
								boxSize={rem(80)}
								borderRadius="full"
								border="1px solid"
								borderColor={errors.icon ? 'error.inputBorder' : 'border.input'}
								bgColor="primary.bgAlpha"
								overflow="hidden"
							>
								{iconURL ? (
									<Image src={iconURL} w="full" h="full" objectFit="cover" />
								) : (
									<DefaultIcon color="primary.fgStrong" pos="relative" left={0.5} top={0.5} />
								)}
							</Center>

							<Flex flexDir="column" alignSelf="stretch" alignItems="flex-start">
								<Controller
									control={control}
									name="icon"
									render={({ field }) => {
										if (isDisabled) {
											return (
												<Button mb={2} isDisabled={true}>
													{t('postCustomization.uploadImageButton')}
												</Button>
											);
										}

										return (
											<HStack mb={2}>
												<UploadButton accept={['jpg', 'png']} onChange={field.onChange}>
													{t('postCustomization.uploadImageButton')}
												</UploadButton>
												{iconURL && (
													<IconButton
														aria-label={t('postCustomization.deleteImage')}
														variant="solid"
														colorScheme="gray"
														size="sm"
														isRound
														onClick={() => field.onChange(null)}
													>
														<TrashOutline size="sm" />
													</IconButton>
												)}
											</HStack>
										);
									}}
								/>
								<FormHelperText my={0}>{t('postCustomization.uploadImageHelper')}</FormHelperText>
							</Flex>
						</HStack>
					</FormControl>

					<FormControl isInvalid={!!errors.headline} isDisabled={isDisabled}>
						<FormLabel>{t('postCustomization.headlineLabel')}</FormLabel>
						<Input type="text" {...register('headline')} />
						<CountHelpText count={headline.trim().length} max={40} />
					</FormControl>

					<FormControl isInvalid={!!errors.reason} isDisabled={isDisabled}>
						<FormLabel>{t('postCustomization.messageLabel')}</FormLabel>
						<Textarea {...register('reason')} minW={rem(56)} />
						<CountHelpText count={reason.trim().length} max={120}>
							{t('postCustomization.messageHelper', {
								context: reasonMessageHelperKey(),
							})}

							<Reveal if={[CelebrationType.Birthday, CelebrationType.WorkAnniversary].includes(type)}>
								{' '}
								{t('postCustomization.messageHelper', { context: type })}
							</Reveal>
						</CountHelpText>
					</FormControl>

					<FormControl isInvalid={!!errors.image} isDisabled={isDisabled}>
						<FormLabel>{t('postCustomization.imageLabel')}</FormLabel>
						<HStack spacing={5}>
							<Center
								boxSize={rem(250)}
								borderRadius="lg"
								border="1px solid"
								borderColor={errors.icon ? 'error.inputBorder' : 'border.input'}
								bgColor="primary.bgAlpha"
								overflow="hidden"
							>
								{imageURL ? (
									<Image src={imageURL} w="full" h="full" objectFit="cover" />
								) : (
									<DefaultImage color="primary.fgStrong" pos="relative" left={0.5} top={0.5} />
								)}
							</Center>

							<Flex maxW={rem(250)} flexDir="column" alignSelf="stretch" alignItems="flex-start" justify="center">
								<Controller
									control={control}
									name="image"
									render={({ field }) => {
										if (isDisabled) {
											return (
												<Button mb={2} isDisabled={true}>
													{t('postCustomization.uploadImageButton')}
												</Button>
											);
										}

										return (
											<>
												<HStack mb={2}>
													<UploadButton accept={['jpg', 'png']} onChange={field.onChange} size="sm">
														{t('postCustomization.uploadImageButton')}
													</UploadButton>
													{imageURL && (
														<IconButton
															aria-label={t('postCustomization.deleteImage')}
															variant="solid"
															colorScheme="gray"
															size="sm"
															isRound
															onClick={() => field.onChange(null)}
														>
															<TrashOutline size="sm" />
														</IconButton>
													)}
												</HStack>
											</>
										);
									}}
								/>
								<FormHelperText my={0}>{t('postCustomization.uploadImageHelper')}</FormHelperText>
							</Flex>
						</HStack>
					</FormControl>

					<Divider my={2} />

					<FormControl>
						<FormLabel>{t('postCustomization.postPreviewLabel')}</FormLabel>
						<FormHelperText mb={4}>
							{(() => {
								switch (type) {
									case CelebrationType.WorkAnniversary:
										return t('postCustomization.postPreviewHelperWorkAnniversary');
									case CelebrationType.Custom:
										return t('postCustomization.postPreviewHelperCustom');
									default:
										return '';
								}
							})()}
						</FormHelperText>
						<FeedPreview
							headline={headline}
							reason={reason}
							amount={amount}
							iconURL={iconURL}
							type={type}
							imageURL={imageURL}
							username={user.username}
							userImage={user.profile_pic_url}
							userDisplayName={user.display_name}
						/>
					</FormControl>
				</FormSection>

				<HStack justify="flex-end">
					{onDelete && (
						<Button colorScheme="secondary" variant="alpha" isLoading={isLoading} onClick={onDelete} isDisabled={isDisabled}>
							{t('deactivate')}
						</Button>
					)}

					<Spacer />
					<Button isDisabled={isDisabled || isSubmitDisabled} isLoading={isLoading} type="submit">
						{t('saveChanges')}
					</Button>
				</HStack>
			</Stack>
		</FormProvider>
	);
};
